import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { JobFilters } from 'api/jobs';
import { getJobTypeDisplayLabel } from 'components/Pages/Jobs/jobTypeLabels';
import { JobStatus, JobType } from 'interfaces/job';
import { compact, map } from 'lodash';
import React from 'react';
import { humanize } from 'utils/helpers';
import { useEncodedFilters } from 'utils/useEncodedFilters';
import { withSlideJobCondition } from './datasetSlideJobCondition';

const jobOptions = () =>
  Object.values(JobType)
    .filter((jobType) => jobType !== JobType.VentanaMock)
    .map((jobType) => ({ value: jobType, label: getJobTypeDisplayLabel(jobType) }));

const statusOptions = () => Object.values(JobStatus).map((status) => ({ value: status, label: humanize(status) }));

const selectedOptions = (values: string[] | undefined, options: Array<{ value: string; label: string }>) =>
  compact(map(values, (value) => options.find((option) => option.value === value)));

const ConditionSelect = ({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: Array<{ value: string; label: string }>;
  value: string[] | undefined;
  onChange: (values: string[]) => void;
}) => (
  <Autocomplete
    multiple
    size="small"
    options={options}
    value={selectedOptions(value, options)}
    onChange={(_, nextValue) => onChange(nextValue.map((option) => option.value))}
    getOptionLabel={(option) => option.label}
    isOptionEqualToValue={(option, selected) => option.value === selected.value}
    sx={{ minWidth: 160, maxWidth: 240, flex: '1 1 160px' }}
    renderInput={(params) => (
      <TextField {...params} placeholder="any" inputProps={{ ...params.inputProps, 'aria-label': label }} />
    )}
  />
);

export const SlideJobCondition = () => {
  const { queryParams, setQueryParams } = useEncodedFilters();
  const datasetJobFilters = queryParams.datasetJobFilters as Partial<JobFilters> | null | undefined;
  const excluded = queryParams.datasetJobFiltersIncludeExclude === 'exclude';

  const writeCondition = (next: { types?: string[]; statuses?: string[] }) => {
    const nextFilters = withSlideJobCondition(datasetJobFilters, next);
    setQueryParams({
      datasetJobFilters: nextFilters,
      datasetJobFiltersIncludeExclude: nextFilters ? queryParams.datasetJobFiltersIncludeExclude : null,
      page: 1,
    });
  };

  return (
    <div
      data-testid="slide-job-condition"
      style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', minWidth: 0 }}
    >
      <Typography
        variant="body2"
        color="text.secondary"
        component="button"
        type="button"
        onClick={() =>
          setQueryParams({
            datasetJobFiltersIncludeExclude: excluded ? null : 'exclude',
            page: 1,
          })
        }
        sx={{ border: 0, background: 'transparent', cursor: 'pointer', p: 0 }}
      >
        {excluded ? 'and not' : 'and'}
      </Typography>
      <Typography variant="body2">has job</Typography>
      <ConditionSelect
        label="Job"
        options={jobOptions()}
        value={datasetJobFilters?.types}
        onChange={(types) => writeCondition({ types, statuses: datasetJobFilters?.statuses })}
      />
      <Typography variant="body2">in status</Typography>
      <ConditionSelect
        label="Status"
        options={statusOptions()}
        value={datasetJobFilters?.statuses}
        onChange={(statuses) => writeCondition({ types: datasetJobFilters?.types, statuses })}
      />
    </div>
  );
};
